import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { EntityTypes, FormTypes } from '../../../../new-lib/constants/common';
import { useContactForm } from '../../hooks/ContactForm';
import { logEvent } from '../../../../new-lib/analytics';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import Select from 'react-select';
import { useQuery } from 'react-query';
import CountrySelect from '../organic-form/components/CountrySelect';
import Dropdown from '../organic-form/components/Dropdown';
import CTA from '../../../../new-components/ui/CTA';
import FormBackground from '../../../../public/assets/images/common/nawy-form.webp';
import MobileFormBackground from '../../../../public/assets/images/common/nawy-form-mobile.webp';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import DownloadDocument from '../../../../new-components/svg-icons/DownloadDocument';
import { ListItemType } from '../organic-form/list-item.type';
import { CompoundsApis } from '../../../../new-lib/apis/compounds.apis';

const SuccessModal = dynamic(() => import('../SuccessModal'), { ssr: false });

interface FormProps {
  isModal: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  areas: any[];
  propertyTypes: any[];
  compound?: {
    id: number;
    name: string;
    nawy_organization_id: number | undefined;
    area?: { id: string; name: string };
  };
}

const NawyNowForm: React.FC<FormProps> = ({
  isModal,
  setModalOpen,
  areas,
  propertyTypes,
  compound
}) => {
  const { t } = useTranslation('nawy-now');
  const { locale } = useRouter();
  const eventId = useSelector((state: any) => state.app.eventId);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState(compound?.name || '');

  const { onPhoneNumberChange, formik, customerNumber } = useContactForm({
    onFormSubmit: () => {
      setSuccessModal(true);
      logEvent.contact('nawy_now_form', 'NawyNow', eventId, {
        cmpnd_id: Number(formik.values.compound.value),
        cmpnd_name: formik.values.compound.label,
        area_id: Number(formik.values.area.value),
        area_name: formik.values.area.label,
        prop_type: formik.values.property_type.label
      });
      setTimeout(() => {
        setSuccessModal(false);
        setModalOpen(false);
      }, 2000);
    },
    formSource: FormTypes.NAWY_NOW
  });

  const { data } = useQuery({
    queryKey: [
      'compounds-autocomplete',
      locale,
      searchInput,
      formik.values.area.value
    ],
    queryFn: async ({ signal }) =>
      CompoundsApis.autocomplete(locale, searchInput, {
        signal,
        areasIds: formik.values.area.value
          ? [Number(formik.values.area.value)]
          : undefined
      }).then(
        data =>
          data?.map((compound: any) => ({
            value: compound.id,
            label: compound.name
          })) ?? []
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity
  });

  useEffect(() => {
    formik.setFieldValue('financing', true);

    //Prevent Image overlay on long press on mobile devices
    const handleTouchStart = (e: any) => {
      e.preventDefault();
    };

    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('touchstart', handleTouchStart);
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('touchstart', handleTouchStart);
      });
    };
  }, []);

  const handleListItemSelection = (
    listName: string,
    selectedItem: ListItemType
  ) => {
    if (listName == 'area') {
      formik.setFieldValue('compound', {});
    }
    formik.setFieldValue(listName, selectedItem);
  };

  return (
    <>
      <Container id="nawy-now-form" isModal={isModal}>
        <ImageWrapper
          src={isMobile ? MobileFormBackground : FormBackground}
          alt="Nawy Now Form Background"
          fill
          style={{ objectFit: 'cover', zIndex: -1 }}
          className="form-background"
        />

        <div className="form-info">
          <div className={`form-header ${isModal ? 'modal-header' : ''}`}>
            <h4 className="headline-2">{t('form_header')}</h4>
            <p className="text-3">{t('form_text')}</p>
          </div>

          {!isMobile && (
            <div className="license-section">
              <div className="subsection">
                <span className="info-title">{t('license_number')}</span>
                <span className="info-text">{t('license')}</span>
              </div>

              <div className="subsection">
                <span className="info-title">{t('email')}</span>
                <span className="info-text">customer-support@nawy.com</span>
              </div>

              <div className="download-document">
                <DownloadDocument />
                <a
                  href={`${process.env.NEXT_PUBLIC_S3_BUCKET_DOMAIN?.replace(
                    's3.eu-central-1.amazonaws.com',
                    ''
                  )}/prod-images.cooingestate.com/docs/دليل+حماية+المتعاملين+في+القطاع+المالي+غير+المصرفي-1.pdf`}
                  target="_blank"
                  className="download-link"
                >
                  {t('download_fra_document')}
                </a>
              </div>
            </div>
          )}
        </div>

        {isMobile && (
          <>
            <CTA entity={{}} entityType={EntityTypes.NAWY_NOW} />
            <div className="divider">
              <span>{t('or')}</span>
            </div>
          </>
        )}

        <form onSubmit={formik.handleSubmit} className="form">
          <div className="input-row">
            <div
              className={
                'form-input' +
                (formik.errors.contact_name && formik.touched.contact_name
                  ? ' input-invalid'
                  : '')
              }
            >
              <input
                type="text"
                placeholder={t('your_name')}
                id="contact_name"
                className="input-field"
                name="contact_name"
                value={formik.values.contact_name}
                onChange={formik.handleChange}
              />
              <div className="form-errors">
                {formik.errors.contact_name && formik.touched.contact_name
                  ? formik.errors.contact_name
                  : null}
              </div>
            </div>
          </div>
          <div className="input-row">
            <div
              className={
                'form-input' +
                (formik.errors.contact_number && formik.touched.contact_number
                  ? ' input-invalid'
                  : '')
              }
            >
              <div
                style={{
                  justifyContent: locale == 'en' ? 'flex-start' : 'flex-end',
                  display: ' flex',
                  flexDirection: 'column',
                  width: '100%',
                  position: 'relative'
                }}
              >
                <PhoneInput
                  value={customerNumber.value}
                  className="phone-input"
                  numberInputProps={{
                    id: 'contact_number',
                    name: 'contact_number',
                    type: 'text',
                    style: {
                      direction: 'ltr',
                      textAlign: locale === 'ar' ? 'right' : 'left',
                      unicodeBidi: 'plaintext'
                    }
                  }}
                  countryCallingCodeEditable={true}
                  defaultCountry={'EG'}
                  countrySelectComponent={props => (
                    <CountrySelect
                      {...props}
                      touched={formik.touched.contact_number}
                      error={formik.errors.contact_number}
                    />
                  )}
                  placeholder={t('phone_number')}
                  focusInputOnCountrySelection
                  onChange={value => {
                    if (value) {
                      onPhoneNumberChange(
                        isValidPhoneNumber((value as string) ?? ''),
                        value
                      );
                      formik.handleChange({
                        target: { name: 'contact_number', value: value }
                      });
                    }
                  }}
                />
              </div>
              <div className="form-errors">
                {formik.errors.contact_number && formik.touched.contact_number
                  ? formik.errors.contact_number
                  : null}
              </div>
            </div>
          </div>

          <div className="input-row">
            <Dropdown className="nawy-now-dropdown" locale={locale}>
              <Select
                options={areas}
                name="location"
                placeholder={t('location')}
                instanceId="location-select"
                onChange={(selected: ListItemType) =>
                  handleListItemSelection('area', selected)
                }
                className="select"
                classNamePrefix="select"
                value={formik.values.area?.value ? formik.values.area : null}
              />
              <div className="form-errors"></div>
            </Dropdown>

            <Dropdown className="nawy-now-dropdown" locale={locale}>
              <Select
                options={data}
                name="compound"
                placeholder={t('compound')}
                instanceId="compound"
                onChange={(selected: ListItemType) =>
                  handleListItemSelection('compound', selected)
                }
                onInputChange={(input: any) => setSearchInput(input)}
                className="select"
                classNamePrefix="select"
                value={
                  formik.values.compound?.value ? formik.values.compound : null
                }
              />
              <div className="form-errors"></div>
            </Dropdown>
          </div>

          <div className="input-row">
            <Dropdown className="property-type-dropdown" locale={locale}>
              <Select
                options={propertyTypes}
                name="property_type"
                placeholder={t('property_type')}
                instanceId="property-type-select"
                onChange={(selected: ListItemType) =>
                  handleListItemSelection('property_type', selected)
                }
                className="select"
                classNamePrefix="select"
                value={
                  formik.values.property_type?.value
                    ? formik.values.property_type
                    : null
                }
              />
              <div className="form-errors">
                {formik.errors.property_type && formik.touched.property_type
                  ? formik.errors.property_type.value
                  : null}
              </div>
            </Dropdown>
          </div>

          <div className="form-input message-box">
            <textarea
              id="message"
              placeholder={t('notes')}
              name="contact_notes"
              className="input-message"
              value={formik.values.contact_notes}
              onChange={formik.handleChange}
            />
          </div>

          <button type="submit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                {t('submitting')}
              </>
            ) : (
              t('submit')
            )}
          </button>
        </form>
      </Container>

      {successModal && <SuccessModal />}
    </>
  );
};

const Container = styled.div<any>`
  position: relative;
  display: flex;
  padding: 40px 80px;
  justify-content: center;
  align-items: center;
  gap: 32px;
  border-radius: 16px;
  margin-top: 40px;
  box-shadow:
    0px 0px 4px 0px rgba(0, 0, 0, 0.04),
    0px 4px 8px 0px rgba(0, 0, 0, 0.06);

  .form-info {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .license-section {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .subsection {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .info-title {
          font-size: 16px;
          font-family: materialBold;
          color: #2c3138;
        }

        .info-text {
          font-size: 14px;
          font-family: materialRegular;
          color: #697586;
        }
      }

      .download-document {
        display: flex;
        align-items: center;
        gap: 8px;

        .download-link {
          font-size: 16px;
          font-family: materialMedium;
          text-decoration: underline;
          text-decoration-style: solid;
          text-decoration-skip-ink: none;
          text-decoration-thickness: auto;
          text-underline-offset: auto;
          color: #2c3138;
          cursor: pointer;
        }
      }
    }
  }

  .form-background {
    position: absolute;
    opacity: 0.08;
  }

  .form-header {
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: 628px;
    align-items: flex-start;
    gap: 24px;
    h4 {
      color: #015c9a;
      font-family: 'materialBold';
      font-size: 40px;
      text-transform: capitalize;
    }
    p {
      color: #2c3138;
      font-family: 'materialMedium';
      font-size: 24px;
      line-height: normal;
    }
  }

  form {
    z-index: 99;
    display: flex;
    max-width: 524px;
    padding: 24px;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    border-radius: 16px;
    background: #fff;
    box-shadow:
      0px 0px 4px 0px rgba(0, 0, 0, 0.04),
      0px 4px 8px 0px rgba(0, 0, 0, 0.06);

    .phone-input {
      width: 100%;
      display: flex;
      align-items: center;
      background-color: ${props => props.theme.light.backgroundColor};
      border-radius: ${props => props.theme.borderRadius};
      border: 1px solid #e6e6e6;

      input {
        width: 100%;
        background-color: ${props => props.theme.light.backgroundColor};
        padding: 16px;
        border: none !important;
        border: 1px solid transparent;
        border-radius: ${props => props.theme.borderRadius};
        border-start-start-radius: 0;
        border-end-start-radius: 0;
        border-inline-start: none;

        ::placeholder {
          font-family: materialRegular;
          font-size: 14px;
        }

        &:focus {
          outline: none !important;
        }
      }
    }

    .input-row {
      display: flex;
      flex-direction: row;
      gap: 16px;
      width: 100%;
      .select__placeholder {
        font-family: materialRegular;
        font-size: 14px !important;
      }
    }

    .form-input {
      display: flex;
      flex-direction: column;
      width: 100%;
      margin-bottom: 8px;
      position: relative;
      label {
        font-size: ${props => props.theme.font.sizes.s};
        font-family: materialMedium;
        color: ${props => props.theme.light.colors.black};
        span {
          color: #ff3a00;
        }
      }
      input {
        width: 100%;
        height: 45px;
        padding: 12px 16px;
        background-color: #ffffff;
        border: 1px solid #e6e6e6;
        border-radius: ${props => props.theme.borderRadius};
        outline: none;
        font-family: materialRegular;
        font-size: 14px !important;
      }
      .form-errors {
        font-size: 10px;
        color: #e70800;
        margin-top: 2.5px;
        margin-inline-start: 5px;
        font-family: materialRegular;
      }
    }

    .message-box {
      width: 100%;
      height: auto;

      textarea {
        min-height: 110px;
        width: 100%;
        padding: 12px 16px;
        background-color: #ffffff;
        border: 1px solid #e6e6e6;
        border-radius: ${props => props.theme.borderRadius};
        font-size: 14px !important;
        outline: none;
        font-family: materialRegular;
        resize: none;
      }
    }

    button {
      width: 100%;
      max-width: 250px;
      height: 45px;
      font-size: 14px;
      border: none;
      background-color: ${props => props.theme.light.colors.primaryBlue};
      color: white;
      border-radius: ${props => props.theme.borderRadius};
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-family: materialRegular;
      cursor: pointer;

      &:hover {
        background-color: ${props => props.theme.light.hoverColors.primaryBlue};
      }
    }
  }
  .headline {
    margin: 8px 0 5px 0;
    h3 {
      font-size: ${props => props.theme.font.sizes.l};
      text-align: center;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.max}) {
    display: flex;
    position: relative;
    padding: 24px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    border-radius: 16px;
    margin-top: 0px;
    box-shadow:
      0px 0px 4px 0px rgba(0, 0, 0, 0.04),
      0px 4px 8px 0px rgba(0, 0, 0, 0.06);
    background-color: rgba(237, 237, 237, 0.3);

    .form-background {
      position: absolute;
      width: 100%;
      height: 100%;
      opacity: 0.08;
    }

    #cta {
      opacity: 0.99;
    }

    .divider {
      display: flex;
      width: 50%;
      justify-content: center;
      align-items: center;
      gap: 16px;
    }

    .divider::before,
    .divider::after {
      content: '';
      background: #d1d4d9;
      height: 1px;
      min-width: 80%;
    }

    .divider span {
      color: #2c3138;
      font-family: 'materialSemiBold';
      font-size: 16px;
    }
    .form-header {
      h4 {
        font-size: 22px;
      }
      p {
        font-size: 16px;
      }
      &.modal-header {
        padding: 50px 0px 0px 0px !important;
      }
    }

    form {
      gap: 8px;
      padding: 18px;
      .input-row {
        gap: 8px;
      }

      button {
        position: ${props => (props.isModal ? 'sticky' : '')};
        bottom: ${props => (props.isModal ? '3%' : '')};
        max-width: none;
      }
    }
  }
`;

export default NawyNowForm;
